import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaUserSecret,
  FaInstagram,
  FaFacebook,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSyncAlt,
  FaPaperPlane,
  FaPlus,
  FaBolt,
  FaBroom,
  FaTrash,
  FaKey,
  FaEnvelope,
  FaBullhorn,
} from 'react-icons/fa';

import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechButton,
  TechButtonGhost,
  TechInput,
  TechSelect,
  TechTextarea,
  StatusPill,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import { listDigitalProfiles, directorBroadcast, amplifyPost, createDigitalProfile, setProfileSessionid, cleanupDummyAccounts } from '../../services/digitalProfilesService';
import ActivadorDummy from './ActivadorDummy';
import EditorCredencialesMasivo from './EditorCredencialesMasivo';
import PanelCredenciales from './PanelCredenciales';
import ConfirmModal from './ConfirmModal';
import GeneradorEmails from './GeneradorEmails';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 0.85rem;
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.9rem 1rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${(p) => p.$accent || accent};
  border-radius: 6px;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:hover {
    background: rgba(0, 180, 216, 0.04);
    border-left-color: ${accent};
  }

  header {
    display: flex;
    gap: 0.55rem;
    align-items: center;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid ${borderSoft};

    .icon {
      width: 34px; height: 34px; border-radius: 6px;
      background: rgba(255, 255, 255, 0.04);
      color: ${(p) => p.$accent || accent};
      display: inline-flex; align-items: center; justify-content: center;
      font-size: 1.05rem;
    }

    .name {
      flex: 1; min-width: 0;
      h3 { margin: 0; font-size: 0.95rem; color: var(--text-primary); font-weight: 600; }
      .sub { font-family: ${mono}; font-size: 0.6rem; letter-spacing: 0.14em; color: var(--text-disabled); text-transform: uppercase; }
    }
  }

  .stats-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.3rem;
    font-family: ${mono};
    font-size: 0.7rem;

    .cell {
      .lbl { color: var(--text-disabled); font-size: 0.55rem; letter-spacing: 0.18em; text-transform: uppercase; }
      .val { color: var(--text-primary); font-size: 0.9rem; font-weight: 600; }
      &.ok .val { color: var(--alert-success, #38d39f); }
      &.err .val { color: var(--alert-error, #ff5470); }
      &.warn .val { color: var(--alert-warning, #f59e0b); }
    }
  }

  .last {
    font-size: 0.78rem;
    color: var(--text-secondary);

    .when { font-family: ${mono}; font-size: 0.6rem; color: var(--text-disabled); letter-spacing: 0.12em; text-transform: uppercase; }
  }
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px; height: 8px; border-radius: 50%;
  background: ${(p) => p.$color || 'gray'};
  margin-right: 6px;
`;

const InfoBox = styled.div`
  padding: 0.85rem 1rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${(p) => p.$tone === 'warn' ? 'var(--alert-warning, #f59e0b)' : accent};
  border-radius: 6px;
  font-size: 0.83rem;
  color: var(--text-secondary);
  line-height: 1.55;

  strong { color: var(--text-primary); }
  code {
    font-family: ${mono};
    font-size: 0.78rem;
    background: rgba(255,255,255,0.05);
    padding: 1px 5px;
    border-radius: 3px;
    color: ${accent};
  }
  ol, ul { margin: 6px 0 0 1.2rem; padding: 0; }
  li { margin-bottom: 3px; }
  .pill {
    display: inline-block;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 2px 7px;
    border-radius: 3px;
    margin-right: 6px;
  }
  .pill-warn { background: rgba(245, 158, 11, 0.15); color: var(--alert-warning, #f59e0b); border: 1px solid rgba(245,158,11,0.3); }
  .pill-ok { background: rgba(56, 211, 159, 0.15); color: var(--alert-success, #38d39f); border: 1px solid rgba(56,211,159,0.3); }
`;

const PlatformIntegrationInfo = ({ platform }) => {
  if (platform === 'instagram') {
    return (
      <InfoBox>
        <span className="pill pill-ok">Instagram · Integrado</span>
        <strong>Cómo obtener el sessionid:</strong>
        <ol>
          <li>Abrí <code>instagram.com</code> en un browser (mejor incógnito).</li>
          <li>Iniciá sesión con la cuenta.</li>
          <li><strong>F12</strong> → pestaña <code>Application</code> (Chrome) o <code>Storage</code> (Firefox).</li>
          <li>Sidebar: <code>Cookies → https://www.instagram.com</code>.</li>
          <li>Buscá la fila <code>sessionid</code>, copia el <strong>value</strong> completo.</li>
        </ol>
        <p style={{ margin: '8px 0 0 0' }}>
          Lo que el sistema hace con la cuenta IG: visit_feed, visit_profile, view_post, fetch_comments,
          like_post (5/día), follow_user (3/día), comment_post (2/día). Todas vía proxy Webshare + rate-limit + horario humano.
        </p>
        <p style={{ margin: '8px 0 0 0', padding: '6px 8px', background: 'rgba(245, 158, 11, 0.08)', borderLeft: '2px solid var(--alert-warning, #f59e0b)', borderRadius: 4 }}>
          <strong>💡 Recomendación:</strong> cargá <strong>2-3 cuentas IG dummy distintas</strong> (con nombres y emails completamente distintos entre sí). Cuando una se flagea, el sistema marca <code>cooldown 48h</code> automático y rota a la siguiente. Sin múltiples cuentas, el flag deja todo parado hasta que IG rehabilite (~24-48h).
        </p>
      </InfoBox>
    );
  }
  if (platform === 'linkedin') {
    return (
      <InfoBox>
        <span className="pill pill-ok">LinkedIn · Integrado (snapshots de followers)</span>
        <strong>Cómo obtener las cookies:</strong>
        <ol>
          <li>Abrí <code>linkedin.com</code> en browser (incógnito ideal).</li>
          <li>Iniciá sesión con la cuenta.</li>
          <li><strong>F12</strong> → <code>Application → Cookies → https://www.linkedin.com</code>.</li>
          <li>Copiá <strong>li_at</strong> (cookie crítica) y <strong>JSESSIONID</strong> (opcional pero útil).</li>
          <li>Pegalos juntos: <code>li_at=AQE...; JSESSIONID=ajax:123...</code></li>
        </ol>
        <p style={{ margin: '8px 0 0 0' }}>
          <strong>Lo que el sistema hace con LinkedIn:</strong> snapshots de followers/connections del perfil.
          Las acciones de escritura (like/comment/follow) <strong>no están implementadas para LinkedIn</strong>
          — LinkedIn detecta scraping muy agresivamente y banea cuentas rápido.
        </p>
      </InfoBox>
    );
  }
  if (platform === 'facebook') {
    return (
      <InfoBox>
        <span className="pill pill-ok">Facebook · Integrado (lectura + escritura)</span>
        <strong>Cómo obtener las cookies:</strong>
        <ol>
          <li>Abrí <code>facebook.com</code> en browser (incógnito ideal).</li>
          <li>Iniciá sesión con la cuenta.</li>
          <li><strong>F12</strong> → <code>Application → Cookies → https://www.facebook.com</code>.</li>
          <li>Copiá <strong>c_user</strong> (tu user_id numérico) y <strong>xs</strong> (la sesión real). Opcionales: <code>fr</code>, <code>datr</code>.</li>
          <li>Pegalos juntos en el textarea con este formato: <code>c_user=123; xs=ABC; fr=DEF; datr=GHI</code></li>
        </ol>
        <p style={{ margin: '8px 0 0 0' }}>
          <strong>Acciones FB:</strong> visit_feed/visit_profile/view_post + fetch_comments (lectura) ·
          like_post (3/día) · unlike_post · follow_user (2/día) · unfollow_user · comment_post (1/día). Todo vía <code>m.facebook.com</code> /
          <code> mbasic.facebook.com</code>. FB es agresivo contra escritura automatizada — usar cuenta vieja
          con historial real es crítico para no ser baneada en horas.
        </p>
      </InfoBox>
    );
  }
  return null;
};

const PLATFORM_META = {
  instagram: { color: '#E1306C', Icon: FaInstagram, label: 'Instagram' },
  facebook:  { color: '#1877F2', Icon: FaFacebook, label: 'Facebook' },
};

const STATUS_COLOR = {
  active: 'var(--alert-success, #38d39f)',
  failed: 'var(--alert-error, #ff5470)',
  challenge_required: 'var(--alert-warning, #f59e0b)',
  banned: 'var(--alert-error, #ff5470)',
  inactive: 'var(--text-disabled)',
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  return `hace ${Math.round(h / 24)}d`;
};

const DigitalProfilesList = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [directorMsg, setDirectorMsg] = useState('');
  const [directorBusy, setDirectorBusy] = useState(false);
  const [directorResult, setDirectorResult] = useState(null);
  const [amplifyUrl, setAmplifyUrl] = useState('');
  const [amplifyPoints, setAmplifyPoints] = useState('');
  const [amplifyBusy, setAmplifyBusy] = useState(false);
  const [amplifyResult, setAmplifyResult] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ platform: 'instagram', username: '', sessionid: '', display_name: '', notes: '' });
  const [newBusy, setNewBusy] = useState(false);
  
  // Estados para cleanup
  const [showCleanup, setShowCleanup] = useState(false);
  const [showCredEditor, setShowCredEditor] = useState(false);
  const [showCredsPanel, setShowCredsPanel] = useState(false);
  const [showEmailGenerator, setShowEmailGenerator] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [confirmCleanup, setConfirmCleanup] = useState(false);

  const load = () => {
    setLoading(true);
    listDigitalProfiles()
      .then((p) => { setProfiles(p); setError(null); })
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newForm.username.trim()) return;
    setNewBusy(true);
    try {
      const created = await createDigitalProfile({
        platform: newForm.platform,
        username: newForm.username.trim().replace(/^@/, ''),
        display_name: newForm.display_name || null,
        notes: newForm.notes || null,
      });
      // si pasó sessionid, cargarlo de inmediato
      if (newForm.sessionid.trim() && created?.id) {
        await setProfileSessionid(created.id, newForm.sessionid.trim());
      }
      setNewForm({ platform: 'instagram', username: '', sessionid: '', display_name: '', notes: '' });
      setShowNewForm(false);
      load();
    } catch (err) {
      alert(friendlyError(err));
    } finally {
      setNewBusy(false);
    }
  };

  const handleDirector = async (e) => {
    e.preventDefault();
    if (!directorMsg.trim()) return;
    setDirectorBusy(true);
    setDirectorResult(null);
    try {
      const result = await directorBroadcast(directorMsg.trim());
      setDirectorResult(result);
      setDirectorMsg('');
      load();
    } catch (err) {
      setDirectorResult({ ok: false, error: friendlyError(err) });
    } finally {
      setDirectorBusy(false);
    }
  };

  const handleAmplify = async (e) => {
    e.preventDefault();
    if (!amplifyUrl.trim()) return;
    setAmplifyBusy(true);
    setAmplifyResult(null);
    try {
      const result = await amplifyPost({
        post_url: amplifyUrl.trim(),
        talking_points: amplifyPoints.trim() || undefined,
      });
      setAmplifyResult(result);
      load();
    } catch (err) {
      setAmplifyResult({ ok: false, error: friendlyError(err) });
    } finally {
      setAmplifyBusy(false);
    }
  };

  // Preview de cuentas a limpiar (sin eliminar aún)
  const handleCleanupPreview = async () => {
    setCleanupBusy(true);
    setCleanupPreview(null);
    setCleanupResult(null);
    try {
      const result = await cleanupDummyAccounts({ dryRun: true, minDays: 1 });
      setCleanupPreview(result);
    } catch (err) {
      alert('Error preview: ' + (friendlyError(err)));
    } finally {
      setCleanupBusy(false);
    }
  };

  // Pide confirmación (modal) antes de la limpieza real
  const handleCleanupExecute = () => {
    if (!cleanupPreview || cleanupPreview.wouldDelete === 0) return;
    setConfirmCleanup(true);
  };

  // Ejecutar limpieza real (tras confirmar en el modal)
  const doCleanupExecute = async () => {
    setCleanupBusy(true);
    try {
      const result = await cleanupDummyAccounts({ dryRun: false, minDays: 1 });
      setCleanupResult(result);
      setCleanupPreview(null);
      setConfirmCleanup(false);
      load(); // Recargar lista
    } catch (err) {
      alert('Error al limpiar: ' + (friendlyError(err)));
      setConfirmCleanup(false);
    } finally {
      setCleanupBusy(false);
    }
  };

  const totals = {
    total: profiles.length,
    active: profiles.filter((p) => p.status === 'active' && p.has_sessionid).length,
    cooldown: profiles.filter((p) => p.in_cooldown).length,
    needsAttention: profiles.filter((p) => ['failed', 'challenge_required', 'banned'].includes(p.status)).length,
  };

  if (loading) {
    return (
      <TechShell>
        <TechPageHeader title="Perfiles Digitales" dossier="DIGI://PROFILES" />
        <Loading>Cargando</Loading>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Perfiles Digitales"
        subtitle="Cuentas dummy con login persistente. Cada perfil simula un usuario real para acceder a contenido protegido."
        dossier="DIGI://PROFILES"
        actions={
          <>
            <TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Refrescar</TechButtonGhost>
            <TechButtonGhost type="button" onClick={() => setShowCredsPanel((v) => !v)} style={{ color: 'var(--alert-success)' }}>
              <FaKey /> {showCredsPanel ? 'Cerrar panel' : 'Ver credenciales'}
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={() => setShowCredEditor((v) => !v)} style={{ color: 'var(--accent)' }}>
              <FaKey /> {showCredEditor ? 'Cerrar credenciales' : 'Editar credenciales'}
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={() => setShowCleanup((v) => !v)} style={{ color: 'var(--alert-warning)' }}>
              <FaBroom /> {showCleanup ? 'Cerrar' : 'Limpiar'}
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={() => setShowEmailGenerator((v) => !v)} style={{ color: accent }}>
              <FaEnvelope /> {showEmailGenerator ? 'Cerrar emails' : 'Generar emails'}
            </TechButtonGhost>
            <TechButton type="button" onClick={() => setShowNewForm((v) => !v)}>
              <FaPlus /> {showNewForm ? 'Cerrar' : 'Nuevo perfil'}
            </TechButton>
          </>
        }
      >
        <StatusPill>Total <strong>{totals.total}</strong></StatusPill>
        <StatusPill>Activas <strong>{totals.active}</strong></StatusPill>
        {totals.cooldown > 0 && <StatusPill>En cooldown <strong>{totals.cooldown}</strong></StatusPill>}
        {totals.needsAttention > 0 && (
          <StatusPill style={{ color: 'var(--alert-error)' }}>
            Requieren atención <strong>{totals.needsAttention}</strong>
          </StatusPill>
        )}
      </TechPageHeader>

      <StatStrip min="170px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaUserSecret /> Perfiles</StatLabel>
          <StatValue>{totals.total}</StatValue>
          <StatSub>{totals.active} listos para usar</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCheckCircle /> Saludables</StatLabel>
          <StatValue>
            {profiles.filter((p) => p.health?.label === 'healthy').length}
          </StatValue>
          <StatSub>success ≥ 90% últimas 24h</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaExclamationTriangle /> Degradadas</StatLabel>
          <StatValue>
            {profiles.filter((p) => ['degraded', 'critical'].includes(p.health?.label)).length}
          </StatValue>
          <StatSub>errores acumulados</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaBolt /> Acciones 24h</StatLabel>
          <StatValue>
            {profiles.reduce((s, p) => s + (p.activity_24h || 0), 0)}
          </StatValue>
          <StatSub>visits, fetches, etc.</StatSub>
        </StatTile>
      </StatStrip>

      {showEmailGenerator && (
        <GeneradorEmails onCreated={load} />
      )}

      {showNewForm && (
        <TechSection title="Nuevo perfil digital" style={{ marginBottom: '1.25rem' }}>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 8 }}>
              <TechSelect value={newForm.platform} onChange={(e) => setNewForm({ ...newForm, platform: e.target.value })}>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="linkedin">LinkedIn</option>
              </TechSelect>
              <TechInput
                placeholder="username (sin @)"
                value={newForm.username}
                onChange={(e) => setNewForm({ ...newForm, username: e.target.value })}
              />
              <TechInput
                placeholder="Nombre a mostrar (opcional)"
                value={newForm.display_name}
                onChange={(e) => setNewForm({ ...newForm, display_name: e.target.value })}
              />
            </div>

            <PlatformIntegrationInfo platform={newForm.platform} />

            <TechTextarea
              placeholder={
                newForm.platform === 'instagram'
                  ? 'sessionid de instagram.com (ver instrucciones arriba). Podés cargarlo después si querés.'
                  : newForm.platform === 'linkedin'
                    ? 'Cookies de linkedin.com pegadas como "li_at=AQ...; JSESSIONID=ajax:123..." (ver instrucciones arriba).'
                    : 'Cookies de facebook.com pegadas como "c_user=...; xs=...; fr=...; datr=..." (ver instrucciones arriba).'
              }
              value={newForm.sessionid}
              onChange={(e) => setNewForm({ ...newForm, sessionid: e.target.value })}
              rows={3}
            />
            <TechInput
              placeholder="Notas (opcional): ej. cuenta vieja con historial, email de registro, etc."
              value={newForm.notes}
              onChange={(e) => setNewForm({ ...newForm, notes: e.target.value })}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <TechButtonGhost type="button" onClick={() => setShowNewForm(false)}>Cancelar</TechButtonGhost>
              <TechButton type="submit" disabled={newBusy || !newForm.username.trim()}>
                <FaPlus /> {newBusy ? '…' : 'Crear perfil'}
              </TechButton>
            </div>
          </form>
        </TechSection>
      )}

      {showCleanup && (
        <TechSection 
          title="🧹 Limpiar cuentas sin actividad" 
          style={{ marginBottom: '1.25rem', borderColor: 'var(--alert-warning)' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
              Elimina cuentas dummy que nunca tuvieron login real ni actividad registrada. 
              Las cuentas con <strong>sessionid</strong>, <strong>cookies</strong> o <strong>requests recientes</strong> no se eliminarán.
            </p>
            
            {!cleanupPreview && !cleanupResult && (
              <div style={{ display: 'flex', gap: 8 }}>
                <TechButton 
                  type="button" 
                  onClick={handleCleanupPreview}
                  disabled={cleanupBusy}
                  style={{ background: 'var(--alert-warning)', borderColor: 'var(--alert-warning)' }}
                >
                  <FaBroom /> {cleanupBusy ? 'Analizando...' : 'Ver cuentas a eliminar'}
                </TechButton>
              </div>
            )}

            {cleanupPreview && (
              <div style={{ 
                background: 'var(--panel-bg)', 
                padding: '12px', 
                borderRadius: '6px',
                border: '1px solid var(--border-soft)'
              }}>
                <h4 style={{ margin: '0 0 12px 0' }}>
                  Se eliminarían {cleanupPreview.wouldDelete} cuentas:
                </h4>
                {cleanupPreview.candidates?.length > 0 ? (
                  <ul style={{ 
                    margin: 0, 
                    padding: '0 0 0 20px', 
                    maxHeight: '200px', 
                    overflowY: 'auto' 
                  }}>
                    {cleanupPreview.candidates.map((acc) => (
                      <li key={acc.id} style={{ marginBottom: '4px', color: 'var(--text-secondary)' }}>
                        <strong>@{acc.username}</strong> · {acc.platform} · {acc.reason}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ color: 'var(--text-secondary)' }}>No hay cuentas para eliminar.</p>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: '12px' }}>
                  <TechButtonGhost type="button" onClick={() => setCleanupPreview(null)}>
                    Cancelar
                  </TechButtonGhost>
                  {cleanupPreview.wouldDelete > 0 && (
                    <TechButton 
                      type="button" 
                      onClick={handleCleanupExecute}
                      disabled={cleanupBusy}
                      style={{ background: 'var(--alert-error)', borderColor: 'var(--alert-error)' }}
                    >
                      <FaTrash /> {cleanupBusy ? 'Eliminando...' : `Eliminar ${cleanupPreview.wouldDelete} cuentas`}
                    </TechButton>
                  )}
                </div>
              </div>
            )}

            {cleanupResult && (
              <div style={{ 
                background: cleanupResult.errors > 0 ? 'rgba(255, 84, 112, 0.1)' : 'rgba(56, 211, 159, 0.1)', 
                padding: '12px', 
                borderRadius: '6px',
                border: `1px solid ${cleanupResult.errors > 0 ? 'var(--alert-error)' : 'var(--alert-success)'}`
              }}>
                <h4 style={{ margin: '0 0 8px 0' }}>
                  ✅ Limpieza completada
                </h4>
                <p style={{ margin: 0 }}>
                  <strong>Eliminadas:</strong> {cleanupResult.deleted} cuentas<br/>
                  {cleanupResult.errors > 0 && (
                    <span style={{ color: 'var(--alert-error)' }}>
                      <strong>Errores:</strong> {cleanupResult.errors}
                    </span>
                  )}
                </p>
                <TechButtonGhost 
                  type="button" 
                  onClick={() => { setCleanupResult(null); setCleanupPreview(null); }}
                  style={{ marginTop: '12px' }}
                >
                  Cerrar
                </TechButtonGhost>
              </div>
            )}
          </div>
        </TechSection>
      )}

      <TechSection
        title="Director General"
        action={<StatusPill>Broadcast a {totals.active} cuentas activas</StatusPill>}
        style={{ marginBottom: '1.25rem' }}
      >
        <form onSubmit={handleDirector} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TechTextarea
            placeholder='Ej: "Visiten el feed", "Simulen sesión humana", "Visiten el perfil de diarioelnortesn"'
            value={directorMsg}
            onChange={(e) => setDirectorMsg(e.target.value)}
            rows={2}
            disabled={directorBusy}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <TechButton type="submit" disabled={directorBusy || !directorMsg.trim()}>
              <FaPaperPlane /> {directorBusy ? 'Enviando…' : `Enviar a ${totals.active}`}
            </TechButton>
          </div>
          {directorResult && (
            <div style={{
              fontFamily: mono,
              fontSize: '0.78rem',
              padding: '0.6rem 0.8rem',
              background: panelDeep,
              border: `1px solid ${borderSoft}`,
              borderLeft: `2px solid ${directorResult.ok ? 'var(--alert-success, #38d39f)' : 'var(--alert-error)'}`,
              borderRadius: 4,
            }}>
              {directorResult.ok
                ? (() => {
                    const rs = directorResult.results || [];
                    // El backend devuelve por cuenta: { ok, error?, tools_executed: [{ ok }] }.
                    // Una cuenta es "ok" si el agente respondió sin error y todas sus
                    // acciones encoladas terminaron bien.
                    const okCount = rs.filter(r => r.ok && !r.error && (r.tools_executed || []).every(t => t.ok)).length;
                    const errCount = rs.length - okCount;
                    const actions = rs.reduce((n, r) => n + (r.tools_executed || []).length, 0);
                    return `Procesado en ${directorResult.processed} cuentas. ${okCount} ok, ${errCount} con error · ${actions} ${actions === 1 ? 'acción ejecutada' : 'acciones ejecutadas'}.`;
                  })()
                : `Error: ${directorResult.error}`}
            </div>
          )}
        </form>
      </TechSection>

      <TechSection
        title="Amplificar posteo"
        action={<StatusPill>Like + comentario IA con el ejército</StatusPill>}
        style={{ marginBottom: '1.25rem' }}
      >
        <form onSubmit={handleAmplify} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TechInput
            placeholder="URL del posteo (Instagram o Facebook). Ej: https://www.instagram.com/p/ABC123/"
            value={amplifyUrl}
            onChange={(e) => setAmplifyUrl(e.target.value)}
            disabled={amplifyBusy}
          />
          <TechTextarea
            placeholder='Ideas para los comentarios (opcional). Ej: "Destacar la gestión en el barrio, tono positivo y cercano."'
            value={amplifyPoints}
            onChange={(e) => setAmplifyPoints(e.target.value)}
            rows={2}
            disabled={amplifyBusy}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <TechButton type="submit" disabled={amplifyBusy || !amplifyUrl.trim()}>
              <FaBullhorn /> {amplifyBusy ? 'Amplificando…' : 'Amplificar'}
            </TechButton>
          </div>
          {amplifyResult && (
            <div style={{
              fontFamily: mono,
              fontSize: '0.78rem',
              padding: '0.6rem 0.8rem',
              background: panelDeep,
              border: `1px solid ${borderSoft}`,
              borderLeft: `2px solid ${amplifyResult.ok ? 'var(--alert-success, #38d39f)' : 'var(--alert-error)'}`,
              borderRadius: 4,
            }}>
              {amplifyResult.ok
                ? (amplifyResult.processed === 0
                    ? (amplifyResult.message || 'No hay cuentas activas para amplificar en esa plataforma.')
                    : `${amplifyResult.likes} likes y ${amplifyResult.comments} comentarios desde ${amplifyResult.processed} cuentas (${amplifyResult.platform}).`)
                : `Error: ${amplifyResult.error}`}
            </div>
          )}
        </form>
      </TechSection>

      {showCredEditor && (
        <EditorCredencialesMasivo profiles={profiles} onSaved={load} />
      )}

      {showCredsPanel && (
        <PanelCredenciales profiles={profiles} onRefresh={load} />
      )}

      {/* Activador Automático de Dummy Accounts */}
      <ActivadorDummy 
        profiles={profiles} 
        onActivate={load}
      />

      {error && <ErrorBox>{error}</ErrorBox>}

      <TechSection title={`Perfiles · ${profiles.length}`}>
        {profiles.length === 0 ? (
          <Empty>
            Aún no hay cuentas dummy cargadas. Andá a <Link to="/settings">Configuración → Cuentas dummy</Link> para agregar.
          </Empty>
        ) : (
          <Grid>
            {profiles.map((p) => {
              const meta = PLATFORM_META[p.platform] || { color: accent, Icon: FaUserSecret, label: p.platform };
              const Icon = meta.Icon;
              const last = p.last_activity;
              return (
                <Card key={p.id} to={`/perfiles-digitales/${p.id}`} $accent={meta.color}>
                  <header>
                    <span className="icon"><Icon /></span>
                    <div className="name">
                      <h3>{p.display_name || p.username}</h3>
                      <div className="sub">
                        @{p.username} · {meta.label}
                        {' · '}
                        <StatusDot $color={STATUS_COLOR[p.status]} />
                        {p.status}
                      </div>
                    </div>
                  </header>

                  <div className="stats-row">
                    <div className={`cell ${p.health?.label === 'healthy' ? 'ok' : p.health?.label === 'critical' ? 'err' : p.health?.label === 'degraded' ? 'warn' : ''}`}>
                      <div className="lbl">Health</div>
                      <div className="val">{p.activity_total > 0 ? `${p.health?.score}%` : '—'}</div>
                    </div>
                    <div className="cell">
                      <div className="lbl">Req 24h</div>
                      <div className="val">{p.activity_24h || 0}</div>
                    </div>
                    <div className={`cell ${p.activity_errors_24h > 0 ? 'err' : ''}`}>
                      <div className="lbl">Err 24h</div>
                      <div className="val">{p.activity_errors_24h || 0}</div>
                    </div>
                  </div>

                  <div className="last">
                    {last ? (
                      <>
                        <span className="when">{fmtRel(last.created_at)}</span> · {last.action}
                        {last.target ? ` → ${String(last.target).slice(0, 30)}` : ''}
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-disabled)', fontStyle: 'italic' }}>Sin actividad registrada</span>
                    )}
                  </div>
                </Card>
              );
            })}
          </Grid>
        )}
      </TechSection>

      <ConfirmModal
        isOpen={confirmCleanup}
        danger
        title="Limpiar cuentas inactivas"
        message={`¿Eliminar ${cleanupPreview?.wouldDelete || 0} cuentas sin actividad real?\n\nEsta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        isLoading={cleanupBusy}
        onConfirm={doCleanupExecute}
        onCancel={() => { if (!cleanupBusy) setConfirmCleanup(false); }}
      />
    </TechShell>
  );
};

export default DigitalProfilesList;
